import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Eye, Award, Shield, Users, TrendingUp, Store, Building2, Utensils, Hotel, School, ShoppingCart } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  const industries = [
    {
      icon: Utensils,
      title: 'Restaurants & Cafes',
      description: 'Food service establishments of all sizes. We supply fresh ingredients, beverages, and kitchen essentials with quality assurance and timely delivery.'
    },
    {
      icon: Hotel,
      title: 'Hotels & Catering',
      description: 'Hospitality businesses and catering services. We provide bulk food supplies, premium brands, and customized solutions for large-scale operations.'
    },
    {
      icon: School,
      title: 'Institutions',
      description: 'Schools, hospitals, offices, and corporate facilities. We offer nutritious food options, compliance support, and budget-friendly institutional pricing.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-14" />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-home">Home</a>
              <a href="/catalog" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-catalog">Products</a>
              <a href="/about" className="text-[#0b1f3f] hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-about">About Us</a>
              <a href="/contact" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-contact">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0b1f3f] via-[#1a3a5c] to-[#0b1f3f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="about-heading">
            About Sai Food House
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Your dedicated wholesale partner specializing in food and beverage distribution across India. We connect trusted brands with retailers, restaurants, and institutions through reliable supply chain solutions.
          </p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4">Our Mission & Vision</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Driving the future of food & beverage distribution with unwavering commitment to quality and service excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl transition-shadow">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#fcce32] bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                  <Target className="w-8 h-8 text-[#fcce32]" />
                </div>
                <h3 className="text-3xl font-bold text-[#0b1f3f]">Our Mission</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                To be the most trusted wholesale distribution partner for food and beverage businesses across India. We are committed to delivering exceptional quality products, reliable supply chain solutions, and outstanding customer service that empowers retailers, restaurants, and institutions to thrive in their markets.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-10 hover:shadow-2xl transition-shadow">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#fcce32] bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                  <Eye className="w-8 h-8 text-[#fcce32]" />
                </div>
                <h3 className="text-3xl font-bold text-[#0b1f3f]">Our Vision</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                To become India's leading food and beverage distribution network, setting new standards in wholesale excellence. We envision expanding our reach nationwide while fostering long-term partnerships that drive mutual growth and contribute to India's thriving food industry ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4">Built on Core Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-[#fcce32] to-[#e0b82a] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-xl">
                <Shield className="w-12 h-12 text-[#0b1f3f]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-3">Integrity</h3>
              <p className="text-gray-600 text-lg">
                Honest, transparent business practices in every interaction
              </p>
            </div>

            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-[#fcce32] to-[#e0b82a] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-xl">
                <Award className="w-12 h-12 text-[#0b1f3f]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-3">Excellence</h3>
              <p className="text-gray-600 text-lg">
                Continuous improvement in service quality and operations
              </p>
            </div>

            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-[#fcce32] to-[#e0b82a] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-xl">
                <Users className="w-12 h-12 text-[#0b1f3f]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-3">Partnership</h3>
              <p className="text-gray-600 text-lg">
                Building lasting relationships that drive mutual success
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted by diverse businesses across India. From small retailers to large institutions, we provide tailored wholesale solutions that drive success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all p-8 group"
                >
                  <div className="w-16 h-16 bg-[#fcce32] bg-opacity-10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-[#fcce32]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0b1f3f] mb-4">{industry.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{industry.description}</p>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="text-[#0b1f3f] border-[#fcce32] hover:bg-[#fcce32] hover:text-[#0b1f3f] font-semibold"
                  >
                    Partner with Us
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0b1f3f] to-[#1a3a5c] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Join hundreds of businesses that trust Sai Food House for their wholesale needs.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate('/catalog')}
              className="h-14 px-10 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a] text-lg font-bold rounded-xl shadow-xl"
            >
              Browse Products
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="h-14 px-10 bg-white text-[#0b1f3f] hover:bg-gray-100 text-lg font-bold rounded-xl border-2"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b1f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-12 mb-4" />
              <p className="text-gray-300">
                Your trusted distributor of quality food & beverage products across India.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Contact Info</h4>
              <p className="text-gray-300 mb-2">82, Chauhan Market Rd, C Block</p>
              <p className="text-gray-300 mb-2">Sector 53, Noida, UP 201307</p>
              <p className="text-gray-300">Phone: 085888 24881</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Quick Links</h4>
              <div className="space-y-2">
                <a href="/catalog" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Products</a>
                <a href="/about" className="block text-gray-300 hover:text-[#fcce32] transition-colors">About Us</a>
                <a href="/contact" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Contact Us</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>© 2025 Sai Food House. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
